import React, { useState, useRef } from 'react';
import axios from 'axios';

const Contact = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({
        success: false,
        message: ''
    });

    const abortControllerRef = useRef(null);

    // Validation functions
    const validateFullName = (name) => {
        if (!name || !name.trim()) return 'Full name is required';
        if (name.trim().length < 2) return 'Full name must be at least 2 characters';
        if (name.trim().length > 100) return 'Full name must be less than 100 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) return 'Full name can only contain letters, spaces, hyphens, and apostrophes';
        return '';
    };

    const validateEmail = (email) => {
        if (!email || !email.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
        if (email.trim().length > 255) return 'Email must be less than 255 characters';
        return '';
    };

    const validatePhoneNumber = (phone) => {
        if (!phone || !phone.trim()) return ''; // Optional field
        
        // Remove any non-digit characters for validation
        const digitsOnly = phone.replace(/\D/g, '');
        
        if (digitsOnly.length > 12) return 'Phone number cannot exceed 12 digits';
        if (digitsOnly.length < 10) return 'Phone number must be at least 10 digits if provided';
        if (!/^[\d\s\+\-\(\)]+$/.test(phone.trim())) return 'Phone number contains invalid characters';
        return '';
    };

    const validateMessage = (message) => {
        if (!message || !message.trim()) return 'Message is required';
        if (message.trim().length < 10) return 'Message must be at least 10 characters';
        if (message.trim().length > 1000) return 'Message must be less than 1000 characters';
        return '';
    };

    // Validate all fields
    const validateForm = () => {
        const newErrors = {
            fullName: validateFullName(formData.fullName),
            email: validateEmail(formData.email),
            phoneNumber: validatePhoneNumber(formData.phoneNumber),
            message: validateMessage(formData.message)
        };

        // Remove empty error messages
        Object.keys(newErrors).forEach(key => {
            if (!newErrors[key]) delete newErrors[key];
        });

        return newErrors;
    };

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle field blur (for validation)
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate the field on blur
        let error = '';
        switch (name) {
            case 'fullName':
                error = validateFullName(formData.fullName);
                break;
            case 'email':
                error = validateEmail(formData.email);
                break;
            case 'phoneNumber':
                error = validatePhoneNumber(formData.phoneNumber);
                break;
            case 'message':
                error = validateMessage(formData.message);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const formErrors = validateForm();
        
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            // Mark all fields as touched to show errors
            setTouched({
                fullName: true,
                email: true,
                phoneNumber: true,
                message: true
            });
            return;
        }

        // Cancel any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            setIsSubmitting(true);
            setSubmitStatus({ success: false, message: '' });

            const response = await axios.post(
                `${process.env.REACT_APP_PUBLIC_API_URL}/submit-contact-request`,
                {
                    fullName: formData.fullName.trim(),
                    email: formData.email.trim().toLowerCase(),
                    phoneNumber: formData.phoneNumber.trim() || undefined,
                    message: formData.message.trim()
                },
                {
                    signal: abortControllerRef.current.signal,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setSubmitStatus({
                    success: true,
                    message: 'Thank you for contacting us! We will get back to you soon.'
                });
                
                // Reset form
                setFormData({
                    fullName: '',
                    email: '',
                    phoneNumber: '',
                    message: ''
                });
                setErrors({});
                setTouched({});
            } else {
                setSubmitStatus({
                    success: false,
                    message: response.data.message || 'Failed to submit form. Please try again.'
                });
            }
        } catch (error) {
            if (error.code !== 'ERR_CANCELED' && error.name !== 'AbortError' && error.name !== 'CanceledError') {
                console.error('Error submitting contact form:', error);
                setSubmitStatus({
                    success: false,
                    message: error.response?.data?.message || 'An error occurred. Please try again later.'
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-24">
            {/* SEO Settings */}
            <title>{`Contact ${process.env.REACT_APP_NAME} – Get Support or Share Your Queries`}</title>
            <meta
                name="description"
                content="Need help or have questions about Hunar Pool? Contact our team for assistance related to jobs, accounts, or platform support."
            />

            <div className="w-full max-w-2xl">
                {/* Hero Section */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Contact <span className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] bg-clip-text text-transparent">Hunar Pool</span>
                    </h1>
                    <p className="text-lg text-gray-300">
                        Get in touch with our team. We're here to help you with your job search, answer questions, 
                        or discuss partnership opportunities.
                    </p>
                </div>

                {/* Contact Form */}
                <div className="bg-gray-900 rounded-2xl p-6 md:p-8 border border-gray-800">
                    <form onSubmit={handleSubmit} noValidate>
                        {/* Full Name Field */}
                        <div className="mb-6">
                            <label className="block text-gray-300 mb-2 font-medium">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 bg-gray-800 border rounded-lg focus:outline-none focus:border-[#0096C7] transition-colors ${
                                    touched.fullName && errors.fullName 
                                        ? 'border-red-500' 
                                        : 'border-gray-700'
                                }`}
                                placeholder="Enter your full name"
                                disabled={isSubmitting}
                            />
                            {touched.fullName && errors.fullName && (
                                <p className="mt-1 text-red-500 text-sm flex items-center">
                                    <i className="fas fa-exclamation-circle mr-2 text-xs"></i>
                                    {errors.fullName}
                                </p>
                            )}
                        </div>

                        {/* Email Field */}
                        <div className="mb-6">
                            <label className="block text-gray-300 mb-2 font-medium">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 bg-gray-800 border rounded-lg focus:outline-none focus:border-[#0096C7] transition-colors ${
                                    touched.email && errors.email 
                                        ? 'border-red-600' 
                                        : 'border-gray-700'
                                }`}
                                placeholder="Enter your email address"
                                disabled={isSubmitting}
                            />
                            {touched.email && errors.email && (
                                <p className="mt-1 text-red-500 text-sm flex items-center">
                                    <i className="fas fa-exclamation-circle mr-2 text-xs"></i>
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* Phone Number Field */}
                        <div className="mb-6">
                            <label className="block text-gray-300 mb-2 font-medium">
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 bg-gray-800 border rounded-lg focus:outline-none focus:border-[#0096C7] transition-colors ${
                                    touched.phoneNumber && errors.phoneNumber 
                                        ? 'border-red-500' 
                                        : 'border-gray-700'
                                }`}
                                placeholder="Enter your phone number (e.g., +1234567890)"
                                disabled={isSubmitting}
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                                <p className="mt-1 text-red-500 text-sm flex items-center">
                                    <i className="fas fa-exclamation-circle mr-2 text-xs"></i>
                                    {errors.phoneNumber}
                                </p>
                            )}                            
                        </div>

                        {/* Message Field */}
                        <div className="mb-6">
                            <label className="block text-gray-300 mb-2 font-medium">
                                Message <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="message"
                                rows={5}
                                value={formData.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 bg-gray-800 border rounded-lg focus:outline-none focus:border-[#0096C7] transition-colors resize-none ${
                                    touched.message && errors.message 
                                        ? 'border-red-500' 
                                        : 'border-gray-700'
                                }`}
                                placeholder="How can we help you?"
                                disabled={isSubmitting}
                            />
                            {touched.message && errors.message && (
                                <p className="mt-1 text-red-500 text-sm flex items-center">
                                    <i className="fas fa-exclamation-circle mr-2 text-xs"></i>
                                    {errors.message}
                                </p>
                            )}
                            <p className="mt-1 text-gray-500 text-xs text-right">
                                {formData.message.length}/1000 characters
                            </p>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20 disabled:opacity-50 disabled:cursor-not-allowed relative"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </span>
                            ) : (
                                'Send Message'
                            )}
                        </button>

                        {/* Status Message */}
                        {submitStatus.message && (
                            <div className={`mt-4 p-4 rounded-lg text-center ${
                                submitStatus.success 
                                    ? 'bg-green-900/20 border border-green-700 text-green-300' 
                                    : 'bg-red-900/20 border border-red-700 text-red-300'
                            }`}>
                                <div className="flex items-center justify-center">
                                    {submitStatus.success ? (
                                        <i className="fas fa-check-circle mr-2 text-green-500"></i>
                                    ) : (
                                        <i className="fas fa-exclamation-circle mr-2 text-red-500"></i>
                                    )}
                                    <span>{submitStatus.message}</span>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Contact;